import { usePopup } from '@/lib/PopupContext';
import { PROCESS_STEPS, TESTIMONIALS } from '@/lib/constants';
import TestimonialSlider from '@/components/shared/TestimonialSlider';
import ProcessSection from '@/components/shared/ProcessSection';
import CTABanner from '@/components/shared/CTABanner';
import { useMeta } from '@/hooks/useMeta';
import { Target, Eye, Compass, Heart, Lightbulb, HandshakeIcon, Building2, Stethoscope, ShoppingBag, Cpu, Utensils, Plane, ArrowRight, CheckCircle } from 'lucide-react';

const VALUES = [
  { icon: Target, title: 'Precision', desc: 'Every application is meticulously prepared to ensure accuracy and maximize approval rates.' },
  { icon: Eye, title: 'Transparency', desc: 'Clear pricing, honest timelines, and regular updates at every stage of the process.' },
  { icon: Compass, title: 'Guidance', desc: 'Expert advice tailored to your specific business needs and intellectual property goals.' },
  { icon: Heart, title: 'Dedication', desc: 'We treat every client\'s brand as if it were our own, providing unwavering commitment.' },
  { icon: Lightbulb, title: 'Innovation', desc: 'Leveraging modern tools and streamlined processes to deliver efficient, quality service.' },
  { icon: HandshakeIcon, title: 'Partnership', desc: 'Building long-term relationships that extend far beyond the initial registration.' },
];

const INDUSTRIES = [
  { icon: Cpu, name: 'Technology & SaaS', desc: 'Software, apps, and digital platform brand protection.' },
  { icon: ShoppingBag, name: 'E-Commerce & Retail', desc: 'Product names, logos, and packaging trademarks.' },
  { icon: Stethoscope, name: 'Healthcare & Pharma', desc: 'Medical brand and pharmaceutical trademark filing.' },
  { icon: Utensils, name: 'Food & Beverage', desc: 'Restaurant brands, product names, and recipe marks.' },
  { icon: Building2, name: 'Real Estate & Finance', desc: 'Financial services and property brand protection.' },
  { icon: Plane, name: 'Travel & Hospitality', desc: 'Hotel chains, travel agencies, and tourism brands.' },
];

const MILESTONES = [
  { number: '01', title: 'Consultation & Strategy', desc: 'We begin with a thorough analysis of your brand, market, and IP goals to develop a tailored protection strategy.' },
  { number: '02', title: 'Research & Due Diligence', desc: 'Our team conducts comprehensive searches across federal, state, and common law databases to identify risks.' },
  { number: '03', title: 'Application & Filing', desc: 'Expert preparation and filing of all necessary documents, ensuring compliance with every regulatory requirement.' },
  { number: '04', title: 'Monitoring & Enforcement', desc: 'Ongoing surveillance and proactive enforcement to protect your brand from infringement long after registration.' },
];

export default function AboutUs() {
  const { openPopup } = usePopup();

  useMeta({
    title: 'About Us | AmericanTrademark Firm - Trusted IP Protection Experts',
    description: 'Learn about AmericanTrademark Firm\'s mission to make intellectual property protection accessible. Expert trademark, copyright, and patent services for businesses of all sizes.',
    keywords: 'about AmericanTrademark Firm, trademark experts, IP protection company, brand protection team, intellectual property services USA',
  });

  return (
    <div data-testid="about-page">
      {/* Hero with Background Image */}
      <section className="relative py-24 md:py-36 overflow-hidden" data-testid="about-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1758518730037-a16581a040e8?w=1920&q=80)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#09090B]/80 via-[#09090B]/65 to-[#09090B]/40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#6B9FFF] mb-4">About Us</p>
            <h1 className="font-heading font-black text-5xl md:text-6xl tracking-tighter leading-none text-white mb-6">
              Protecting Brands,<br />Building Confidence
            </h1>
            <p className="text-lg md:text-xl text-zinc-300 font-body leading-relaxed">
              AmericanTrademark Firm is dedicated to providing accessible, professional intellectual property services to businesses across the United States and beyond. We believe every brand deserves strong legal protection.
            </p>
          </div>
        </div>
      </section>

      {/* 1. Mission */}
      <section className="py-24 md:py-32 bg-white" data-testid="about-mission">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1672380135241-c024f7fbfa13?w=800&q=80"
                alt="Professional business meeting"
                className="w-full h-[450px] object-cover"
                loading="lazy"
              />
            </div>
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-3">Our Mission</p>
              <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-[#09090B] leading-tight mb-6">
                Making IP Protection Accessible
              </h2>
              <p className="text-base md:text-lg text-zinc-600 font-body leading-relaxed mb-4">
                We started with a simple mission: to make trademark registration and intellectual property protection accessible to businesses of every size. Too many entrepreneurs and companies leave their brands unprotected because the process seems complex or expensive.
              </p>
              <p className="text-base text-zinc-600 font-body leading-relaxed mb-4">
                Our team combines deep legal expertise with modern technology to streamline the registration process, reduce costs, and deliver exceptional results. We handle the complexity so our clients can focus on what they do best — building their brands.
              </p>
              <p className="text-base text-zinc-600 font-body leading-relaxed">
                From startups filing their first trademark to established corporations managing global IP portfolios, we provide personalized service that scales with your business needs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Values */}
      <section className="py-24 md:py-32 bg-[#F4F4F5]" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-3">Our Values</p>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-[#09090B] leading-tight">
              What Drives Us Forward
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0">
            {VALUES.map((val, idx) => {
              const Icon = val.icon;
              return (
                <div key={idx} className="border border-[#E4E4E7] bg-white p-8" data-testid={`value-${idx}`}>
                  <Icon className="w-8 h-8 text-[#002FA7] mb-4" />
                  <h3 className="font-heading font-bold text-lg text-[#09090B] mb-3">{val.title}</h3>
                  <p className="text-sm text-zinc-600 font-body leading-relaxed">{val.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4. How We Work - Milestones */}
      <section className="py-24 md:py-32 bg-white" data-testid="about-how-we-work">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-3">How We Work</p>
              <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-[#09090B] leading-tight mb-6">
                A Proven Approach to IP Protection
              </h2>
              <p className="text-base text-zinc-600 font-body leading-relaxed mb-6">
                Our structured methodology has been refined over thousands of successful filings. Every step is designed to maximize your chances of approval while minimizing time and cost.
              </p>
              <button
                onClick={() => openPopup()}
                className="bg-[#002FA7] text-white hover:bg-[#002480] transition-colors px-8 py-4 font-bold text-sm tracking-widest uppercase inline-flex items-center gap-2"
                data-testid="about-howwework-cta"
              >
                Get Started <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="md:col-span-8">
              <div className="space-y-0">
                {MILESTONES.map((m, idx) => (
                  <div key={idx} className="flex gap-6 border-b border-[#E4E4E7] py-8 first:pt-0 last:border-b-0" data-testid={`milestone-${idx}`}>
                    <span className="font-heading font-black text-5xl text-[#002FA7] opacity-15 leading-none shrink-0 w-16">
                      {m.number}
                    </span>
                    <div>
                      <h3 className="font-heading font-bold text-xl text-[#09090B] mb-2">{m.title}</h3>
                      <p className="text-sm text-zinc-600 font-body leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Industries We Serve */}
      <section className="py-24 md:py-32 bg-[#F4F4F5]" data-testid="about-industries">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-3">Industries We Serve</p>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-[#09090B] leading-tight">
              Trusted Across Sectors
            </h2>
            <p className="text-base md:text-lg text-zinc-600 mt-4 max-w-2xl mx-auto font-body">
              From tech startups to established enterprises, we protect brands in every major industry.
            </p>
          </div>
          <div className="mobile-slider md:grid-cols-2 lg:grid-cols-3 gap-0">
            {INDUSTRIES.map((ind, idx) => {
              const Icon = ind.icon;
              return (
                <div key={idx} className="border border-[#E4E4E7] bg-white p-8" data-testid={`industry-${idx}`}>
                  <Icon className="w-8 h-8 text-[#002FA7] mb-4" />
                  <h3 className="font-heading font-bold text-lg text-[#09090B] mb-2">{ind.name}</h3>
                  <p className="text-sm text-zinc-600 font-body leading-relaxed">{ind.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 6. Testimonials */}
      <TestimonialSlider />

      {/* 7. Approach */}
      <section className="py-24 md:py-32 bg-white" data-testid="about-approach">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-3">Our Approach</p>
              <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-[#09090B] leading-tight mb-6">
                Client-First, Results-Driven
              </h2>
              <p className="text-base md:text-lg text-zinc-600 font-body leading-relaxed mb-4">
                Every business has unique intellectual property needs. That's why we don't offer cookie-cutter solutions. Our approach begins with understanding your brand, your market, and your goals — then crafting a protection strategy that fits.
              </p>
              <p className="text-base text-zinc-600 font-body leading-relaxed mb-6">
                We maintain open communication throughout the process, providing regular updates and expert guidance at every milestone. Our clients never feel left in the dark about the status of their applications.
              </p>
              <div className="space-y-3 mb-8">
                {['Personalized strategy for every client', 'Regular progress updates and transparent communication', 'Expert guidance from filing to approval'].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#002FA7] shrink-0" />
                    <span className="text-sm text-zinc-700 font-body">{item}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => openPopup()}
                className="bg-[#002FA7] text-white hover:bg-[#002480] transition-colors px-8 py-4 font-bold text-sm tracking-widest uppercase"
                data-testid="about-cta-btn"
              >
                Start Your Journey
              </button>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1625461291092-13d0c45608b3?w=800&q=80"
                alt="Modern office"
                className="w-full h-[450px] object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 8. Process */}
      <ProcessSection />

      {/* 9. CTA */}
      <CTABanner title="Ready to Protect Your Brand?" subtitle="Let our team of experts guide you through the trademark registration process. Get started today." />
    </div>
  );
}
